import { createEntityRecord, patchEntityRecord } from '@kizen/api/records';
import { useState, useCallback, useMemo, useRef, cloneElement } from 'react';
import { useClickAway } from 'react-use';
import Overlay from 'react-bootstrap/Overlay';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

import KizenTypography from 'app/kizentypo';
import { getAppRoot } from 'utility/app';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import { EmptyCell } from 'components/Kizen/Table';
import { snakeToCamelCaseKeys } from 'services/helpers';
import {
  UnarchiveConflictModal,
  UnarchiveForbiddenModal,
} from 'components/Modals/UnarchiveModal';
import {
  PERMISSION_DENIED,
  UNARCHIVE_CONFLICT,
  UNARCHIVE_PROMPT,
  UNARCHIVE,
} from 'components/Modals/CreateEntity/helpers';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import {
  getAuthTeamMember,
  getBusinessTimeZone,
} from 'store/authentication/selectors';
import {
  cloneFormattedElement,
  getCreationFieldIds,
  getNextField,
} from './helpers';
import { ErrorCard, TRow } from './styles';
import { PADDING_IN_PIXELS } from 'pages/Common/components/BigTableCells';
import { DEFAULT_FIELD_NAMES, FIELD_TYPES } from 'utility/constants';

export default function AddRecordTRow({
  model,
  addingId,
  fields,
  getColumns,
  columnSettings,
  onSubmit,
  onAddingId,
  teams = [],
  stages = [],
  handleUpdateTableRecords,
  ...others
}) {
  const teamMember = useSelector(getAuthTeamMember);
  const businessTimezone = useSelector(getBusinessTimeZone);

  const [creationFieldId, conflictFieldId] = useMemo(
    () => getCreationFieldIds({ fields, model }),
    [model, fields]
  );

  const ownerField = useMemo(() => {
    return fields.find((field) => field.name === 'owner');
  }, [fields]);

  const { t } = useTranslation();
  const preReleaseFeatures = usePreReleaseFeatures();
  const [isUnarchiveForbidden, setIsUnarchiveForbidden] = useToggle(false);
  const [isUnarchiveConflict, setIsUnarchiveConflict] = useToggle(false);
  const [unArchiveConflictMessage, setUnArchiveConflictMessage] = useState('');

  const [newRecord, setNewRecord] = useState(() => {
    const state = {
      access: { view: true, edit: true },
      id: 'new',
      fields: {},
      object_type: model.objectType,
    };

    if (ownerField) {
      state.fields[ownerField.id] = {
        id: ownerField.id,
        name: ownerField.name,
        display_name: ownerField.displayName,
        field_type: ownerField.fieldType,
        value: {
          deleted: false,
          email: teamMember.email,
          first_name: teamMember.firstName,
          id: teamMember.id,
          last_name: teamMember.lastName,
        },
      };
    }

    return state;
  });

  const readyRecord = useRef();
  const [autoFocusField, setAutoFocusField] = useState(() =>
    getNextField(
      {
        getColumns,
        columnSettings,
        fields,
        model,
        teams,
        businessTimezone,
        t,
        preReleaseFeatures,
      },
      creationFieldId
    )
  );

  const [showOverlay, setShowOverlay] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [uniqueNameError, setUniqueNameError] = useState('');

  const submittingRef = useRef(false);
  const clickAwayCount = useRef(0);
  const queuedFieldValues = useRef({});
  const overlayTarget = useRef();
  const currentColumnRef = useRef();
  const rowRef = useRef();

  const fieldValueOverrides = useMemo(() => {
    const result = {};

    for (const fv of Object.values(newRecord.fields)) {
      // this condition occurs when the owner column is to the left of the name column (which is when the create API call is made).
      if (fv.name === 'owner' && fv.value && 'shortLabel' in fv.value) {
        result[fv.id] = fv.value;
      }
    }

    return result;
  }, [newRecord]);

  const hideMessage = () => {
    if (showMessage) {
      setTimeout(() => setShowMessage(false), 1500);
    } else {
      setShowOverlay(false);
    }
  };

  const onSubmitRecord = useCallback(
    async ({ column, values, unarchive = UNARCHIVE_PROMPT }) => {
      if (clickAwayCount.current >= 1 && column.id === conflictFieldId) {
        return false;
      }

      setShowOverlay(false);
      submittingRef.current = column.id;

      let nextRecord = queuedFieldValues.current;

      if (unarchive === UNARCHIVE_PROMPT) {
        nextRecord = values.reduce((acc, { field, value }) => {
          if (value !== null && value !== undefined) {
            acc.fields[field.id] = {
              display_name: field.displayName,
              id: field.id,
              field_type: field.fieldType,
              name: field.name,
              value,
            };
          }
          return acc;
        }, newRecord);

        queuedFieldValues.current = {
          ...queuedFieldValues.current,
          ...values.reduce((acc, v) => {
            acc[v.field.id] = v;
            return acc;
          }, {}),
        };
      }

      const allValues = [...Object.values(queuedFieldValues.current)];
      const updates = allValues
        .filter((v) => Boolean(v.value))
        .map((v) => {
          return { id: v.field.id, value: v.value };
        });

      if (!updates.length) {
        return true;
      }

      try {
        if (newRecord.id === 'new') {
          if (
            allValues.some(
              (v) =>
                v.field.name === creationFieldId ||
                v.field.name === conflictFieldId
            )
          ) {
            nextRecord = await createEntityRecord(AxiosService, model.id, {
              fields: updates,
              unarchive,
            });
            queuedFieldValues.current = {};
          }
        } else {
          nextRecord = await patchEntityRecord(
            AxiosService,
            model.id,
            newRecord.id,
            { fields: updates },
            { params: { return_all_fields: true } }
          );
          queuedFieldValues.current = {};
        }

        const nextField = getNextField(
          {
            getColumns,
            columnSettings,
            fields,
            model,
            teams,
            t,
          },
          creationFieldId,
          column.id || creationFieldId
        );

        readyRecord.current = {
          ...newRecord,
          ...nextRecord,
          fields: {
            ...newRecord.fields,
            ...nextRecord.fields,
          },
        };

        if (
          unarchive === UNARCHIVE ||
          (column && nextField === null && newRecord.id !== 'new') ||
          clickAwayCount.current > 1
        ) {
          return onSubmit(readyRecord.current);
        }

        if (currentColumnRef.current === column.id) {
          setAutoFocusField(nextField);
        }

        onAddingId(nextRecord.id);
        setNewRecord(readyRecord.current);
        clickAwayCount.current = 0;
        return true;
      } catch (error) {
        const orig = snakeToCamelCaseKeys(getOriginalError(error));
        if (orig?.code) {
          setAutoFocusField(null);
          if (orig.code === PERMISSION_DENIED) {
            setIsUnarchiveForbidden();
            setUnArchiveConflictMessage(orig.detail);
          }
          if (orig.code === UNARCHIVE_CONFLICT) {
            setIsUnarchiveConflict();
            setUnArchiveConflictMessage(orig.detail);
          }
        }

        const index = orig.errors.fields?.findIndex(Boolean) ?? -1;

        if (index >= 0) {
          const errorField = allValues[index].field;

          setUniqueNameError(orig.errors.fields[index].value[0]);
          setTimeout(() => {
            setAutoFocusField(
              errorField.isDefault ? errorField.name : errorField.id
            );
            setShowOverlay(true);
            setShowMessage(true);
          }, 2);
        } else if (orig.code === 'conflict' && orig.detail) {
          setUniqueNameError(orig.detail);
          setTimeout(() => {
            setAutoFocusField(conflictFieldId);
            setShowOverlay(true);
            setShowMessage(true);
          }, 2);
        }
        return false;
      } finally {
        await new Promise((res) => setTimeout(res, 1));
        submittingRef.current = false;
      }
    },
    [
      fields,
      getColumns,
      creationFieldId,
      conflictFieldId,
      columnSettings,
      onAddingId,
      onSubmit,
      model,
      teams,
      newRecord,
      t,
      setIsUnarchiveConflict,
      setIsUnarchiveForbidden,
      setUnArchiveConflictMessage,
    ]
  );

  const columns = useMemo(() => {
    const cols = getColumns({
      model,
      fields,
      columnSettings,
      onSubmitRecord,
      onSubmitContact: onSubmitRecord,
      teams,
      stages,
      businessTimezone,
      t,
      preReleaseFeatures,
      handleUpdateTableRecords,
    });
    const eventHandler = (col, ev) => {
      if (
        col.isRequired ||
        col.id === conflictFieldId ||
        col.id === creationFieldId
      ) {
        overlayTarget.current = ev.currentTarget;
        overlayTarget.current.errorMessage = col.errorMessage;
      } else if (submittingRef.current !== conflictFieldId) {
        overlayTarget.current = null;
      }
      if (autoFocusField !== col.id) setAutoFocusField(null);
      currentColumnRef.current = col.id;
    };

    cols.forEach((col) => {
      if (col.id !== 'columns' && (col.cell || col.bodyCell)) {
        if (col.id === 'selection') {
          col.cell = <EmptyCell />;
        } else if (col.cell) {
          col.cell = cloneElement(col.cell, {
            onFocus: (ev) => {
              eventHandler(col, ev);
            },
            onClick: (ev) => {
              clickAwayCount.current = 0;
              eventHandler(col, ev);
            },
            shouldFocusNext: true,
          });
        }
        if (col.bodyCell) {
          col.bodyCell.shouldFocusNext = true;
          const field = col.bodyCell.field || col.field;
          if (field) {
            col.bodyCell.keyListenerId = `${field.id}-new`;
          }
        }
        col.format = cloneFormattedElement(col.format, {
          emptyPlaceholder: '',
          isAdding: true,
          shouldFocusNext: true,
          autoFocus: autoFocusField === col.id,
          submitUnchanged: [
            conflictFieldId,
            creationFieldId,
            DEFAULT_FIELD_NAMES.owner,
            DEFAULT_FIELD_NAMES.stage,
          ].some((el) => col.id === el),
        });

        col.onFocus = (ev) => {
          eventHandler(col, ev);
        };
        col.onClick = (ev) => {
          clickAwayCount.current = 0;
          eventHandler(col, ev);
        };
        col.autoFocus = autoFocusField === col.id;
      }
    });
    return cols;
  }, [
    model,
    fields,
    columnSettings,
    autoFocusField,
    conflictFieldId,
    creationFieldId,
    onSubmitRecord,
    teams,
    stages,
    businessTimezone,
    t,
    getColumns,
    preReleaseFeatures,
    handleUpdateTableRecords,
  ]);

  useClickAway(
    rowRef,
    async (ev) => {
      if (!getAppRoot().contains(ev.target)) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve));
      clickAwayCount.current += 1;
      if (clickAwayCount.current > 1) {
        if (newRecord.id === 'new') {
          onAddingId(null);
        } else {
          onSubmit(readyRecord.current);
        }
      }
    },
    ['mousedown']
  );

  const handleUnarchive = useCallback(
    async (unarchive) => {
      clickAwayCount.current = 0;
      await onSubmitRecord({
        column: { id: currentColumnRef.current },
        unarchive,
        values: queuedFieldValues.current,
      });
      setIsUnarchiveConflict();
      setUnArchiveConflictMessage('');
    },
    [onSubmitRecord, setIsUnarchiveConflict]
  );

  const handleHide = () => {
    setIsUnarchiveConflict();
    setAutoFocusField(conflictFieldId);
    setUnArchiveConflictMessage('');
  };

  const hideForbiddenModal = () => {
    setIsUnarchiveForbidden();
    setAutoFocusField(conflictFieldId);
    setUnArchiveConflictMessage('');
  };

  const submitUnchangedList = useMemo(
    () => [
      conflictFieldId,
      creationFieldId,
      DEFAULT_FIELD_NAMES.owner,
      DEFAULT_FIELD_NAMES.stage,
      ...columns
        .filter(
          (col) =>
            col.bodyCell?.field?.fieldType === FIELD_TYPES.PhoneNumber.type
        )
        .map(({ id }) => id),
    ],
    [columns, conflictFieldId, creationFieldId]
  );

  return (
    <>
      <TRow
        ref={rowRef}
        columns={columns}
        data={newRecord}
        autoFocusField={autoFocusField}
        submitUnchangedList={submitUnchangedList}
        fieldValues={fieldValueOverrides}
        fixWidthStyle={{
          padding: PADDING_IN_PIXELS,
        }}
        {...others}
      />
      <Overlay
        transition={false}
        target={overlayTarget.current}
        show={showOverlay}
        placement="bottom-start"
      >
        <ErrorCard show={showMessage} onTransitionEnd={hideMessage}>
          <KizenTypography>
            {uniqueNameError || overlayTarget.current?.errorMessage}
          </KizenTypography>
        </ErrorCard>
      </Overlay>
      <UnarchiveForbiddenModal
        show={isUnarchiveForbidden}
        onConfirm={hideForbiddenModal}
        onHide={hideForbiddenModal}
        message={unArchiveConflictMessage}
      />
      <UnarchiveConflictModal
        show={isUnarchiveConflict}
        onConfirm={() => handleUnarchive(UNARCHIVE)}
        onAdditionalConfirm={() => handleUnarchive('overwrite')}
        onHide={handleHide}
        message={unArchiveConflictMessage}
      />
    </>
  );
}
